import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Center,
  Spinner,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { groupBy, isNil, round, sumBy } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import invariant from 'tiny-invariant';
import { useFlag } from '@unleash/proxy-client-react';
import BufferOp from 'jsts/org/locationtech/jts/operation/buffer/BufferOp';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'urql';
import {
  FRIENDLY_PROPERTIES,
  FriendlyAttribute,
  displayNameFromAlgorithm,
  displayNameFromFriendlyAttribute,
  displayNameFromProperty,
} from '../../domain/friendly-properties';
import { GeometricAlgorithmType } from '../../domain/geometry/algorithm-types';
import {
  MultiPolygon,
  MultiPolylineResult,
  OperationResult,
  Point,
  ResultType,
  Triangle,
  TriangulatedSurfaceResult,
} from '../../domain/geometry/geometric-types';
import {
  calculatePlaneEquation,
  to3dMultiPolygon,
  toPlanarMultiPolygon,
} from '../../domain/geometry/algorithms/util/plane';
import {
  toJstsMultiPolygon,
  toMultiPolygon2,
} from '../../domain/geometry/algorithms/util/polygon';
import { Property, PropertyType } from '../../domain/property-types';
import { useDebouncedLoading } from '../../hooks/debounced-loading';
import { useGeometricAlgorithms } from '../../hooks/geometric-algorithms';
import { useLocalStorage } from '../../hooks/local-storage';
import { usePropertyContext } from '../../hooks/property-resolving';
import { useShapes } from '../../hooks/shapes';
import { OnDemandGeometryVisualization } from '../../services/viewer/result-visualisation/OnDemandGeometryVisualization';
import { getShapeUrn } from '../../services/viewer/ShapesManager';
import { formatDragData } from '../../utils/property-dragging-utils';

import { calculateGeometry } from '../../domain/geometric-operations';
import { getMappingForTargetType } from '../../domain/geometry/algorithm-map';
import { convertToElementIdentifierDbidType } from '../../utils/dbid-utils';
import {
  convertSIQuantity,
  formatUnit,
  roundingToPrecision,
  unitToUnitType,
} from '../../utils/unit-utils';
import { getCategoriesWithFriendlyName } from '../orderContractor/CategoriesMenuListCheckBoxes';
import { ShapeDrawingResultType } from '../orderContractor/shapes/ShapeDrawing';
import PlaceholderText from '../text/PlaceholderText';
import { DiffNumber } from '../project/project-history/DiffNumber';
import { PropertiesAccordionBody } from './BIMPropertiesAccordionBody';
import { DbIdsPerModel, useViewer } from './ForgeViewer';
import StyledKeyValuesProperties, {
  KeyValue,
} from './StyledKeyValuesProperties';
import {
  CreateShapeDocument,
  CreateShapeInput,
  ProjectSettingsType,
} from 'src/gql/graphql';
import { EventType } from 'src/gql/graphql';

// Helper function to separate location algorithms from other algorithms
const separateLocationAlgorithms = (
  algorithmResults: Map<GeometricAlgorithmType, OperationResult[]>
) => {
  const locationResults = new Map<GeometricAlgorithmType, OperationResult[]>();
  const calculationResults = new Map<
    GeometricAlgorithmType,
    OperationResult[]
  >();

  algorithmResults.forEach((results, algorithmType) => {
    if (String(algorithmType).includes('location')) {
      locationResults.set(algorithmType, results);
    } else {
      calculationResults.set(algorithmType, results);
    }
  });

  return { locationResults, calculationResults };
};

// Helper: compute EventType from a numeric diff
const computeEventType = (diff?: {
  old?: unknown;
  new?: unknown;
}): EventType | undefined => {
  if (!diff) return undefined;
  const hasOld = typeof diff.old === 'number';
  const hasNew = typeof diff.new === 'number';
  if (hasNew && !hasOld) return EventType.Created;
  if (hasOld && !hasNew) return EventType.Deleted;
  if (
    hasOld &&
    hasNew &&
    Math.abs((diff.old as number) - (diff.new as number)) > 0.000001
  )
    return EventType.Modified;
  return undefined;
};

// Helper: get precision from unit settings based on the result units
const getPrecisionFromUnitSettings = (
  resultUnits: string | undefined,
  unitSettings: ProjectSettingsType['unitSettings']
): number => {
  if (!resultUnits || !unitSettings) return 2;
  const unitType = unitToUnitType(resultUnits);
  if (!unitType) return 2;
  const rounding = unitSettings[unitType]?.rounding;
  return rounding ? roundingToPrecision(rounding) : 2;
};

// Helper: create a line shape input from a MultiPolylineResult
const getLineShapeInputFromResult = (
  result: MultiPolylineResult,
  projectId: string,
  shapeName: string,
  unitScale: number
): CreateShapeInput => {
  // Extract points from the first line in the multipolyline
  const points = result.multiPolyline.lines[0];
  return {
    shape: {
      projectId,
      name: shapeName,
      urn: getShapeUrn(projectId),
      line: {
        create: [
          {
            points: points.map((point) => ({
              x: point[0],
              y: point[1],
              z: point[2],
            })),
            unitScale,
          },
        ],
      },
    },
  };
};

// Helper: create a polygon shape input from a TriangulatedSurfaceResult
const getPolygonShapeInputFromTriangles = (
  result: TriangulatedSurfaceResult,
  projectId: string,
  shapeName: string,
  unitScale: number
): CreateShapeInput => {
  const { triangles } = result;

  // Calculate plane from the first triangle
  const plane = calculatePlaneEquation(triangles[0]);

  // Convert triangles to a 3D MultiPolygon
  const trianglesAsMultiPolygon: MultiPolygon = {
    polygons: triangles.map((triangle: Triangle) => ({
      exterior: [...triangle, triangle[0]], // Close the ring
      interiors: [],
    })),
  };

  // Project to 2D, union using buffer, then project back to 3D
  const multiPolygon2d = toPlanarMultiPolygon(trianglesAsMultiPolygon, plane);
  const jstsMultiPolygon = toJstsMultiPolygon(multiPolygon2d);
  const unionedGeometry = BufferOp.bufferOp(jstsMultiPolygon, 0, 0);
  const unionedMultiPolygon2d = toMultiPolygon2(unionedGeometry);
  const unionedMultiPolygon3d = to3dMultiPolygon(unionedMultiPolygon2d, plane);

  // Convert to the shape input format
  const polygons = unionedMultiPolygon3d.polygons.map((polygon) => ({
    exterior: {
      points: polygon.exterior.map((point) => ({
        x: point[0],
        y: point[1],
        z: point[2],
      })),
    },
    interiors: polygon.interiors.map((interior) => ({
      points: interior.map((point) => ({
        x: point[0],
        y: point[1],
        z: point[2],
      })),
    })),
  }));

  return {
    shape: {
      projectId,
      name: shapeName,
      urn: getShapeUrn(projectId),
      polygon: {
        create: [
          {
            plane: {
              a: plane.unitNormal[0],
              b: plane.unitNormal[1],
              c: plane.unitNormal[2],
              d: plane.planeCoefficient,
            },
            multipolygon: { polygons },
            unitScale,
          },
        ],
      },
    },
  };
};

// Helper: create shape input from a geometric result
const getShapeInputFromResult = (
  result: MultiPolylineResult | TriangulatedSurfaceResult,
  projectId: string,
  shapeName: string,
  unitScale: number
): CreateShapeInput | null => {
  if (result.type === ResultType.MULTIPOLYLINE) {
    return getLineShapeInputFromResult(result, projectId, shapeName, unitScale);
  } else if (result.type === ResultType.TRIANGULATED_SURFACE) {
    return getPolygonShapeInputFromTriangles(
      result,
      projectId,
      shapeName,
      unitScale
    );
  }
  return null;
};

// eslint-disable-next-line complexity
export default function PropertiesTab({
  properties,
  selectedDbIds,
  projectId,
  projectSettings,
  referenceResolvedProperties,
  propertyDiffMap,
  referencePropertyContext,
  newPropertyContextForDiff,
  isFreeContractor,
}: {
  properties: Property[];
  selectedDbIds: DbIdsPerModel;
  projectId: string;
  projectSettings?: ProjectSettingsType;
  referenceResolvedProperties?: Property[] | null;
  propertyDiffMap?: Record<string, { old?: unknown; new?: unknown }>;
  referencePropertyContext?: any;
  newPropertyContextForDiff?: any;
  isFreeContractor?: boolean;
}) {
  const { propertyContext, isAllPropertiesLoaded } =
    usePropertyContext(projectId);

  const enableUnitSettings = useFlag('enable-unit-settings');
  const enableCreateShapesFromAlgorithmResults = useFlag(
    'enable-create-shapes-from-algorithm-results'
  );

  const geometricAlgorithms = useGeometricAlgorithms();

  const [algorithmResults, setAlgorithmResults] = useState<
    Map<GeometricAlgorithmType, OperationResult[]>
  >(new Map());

  const [visualizedAlgorithmResult, setVisualizedAlgorithmResult] = useState<
    OperationResult[]
  >([]);

  const [expandedAccordions, setExpandedAccordions] = useLocalStorage<
    number | number[]
  >(`${projectId}-properties-tab-accordion-indices`, [0]);

  const [isLoading, setIsLoading] = useDebouncedLoading();

  const { t } = useTranslation(['common', 'project']);
  const toast = useToast();

  const { viewer } = useViewer();
  const { getNewShapeName } = useShapes(projectId);
  const [, createShapeMutation] = useMutation(CreateShapeDocument);

  // Callback to create shapes from geometric results (lines or polygons)
  const handleCreateShape = useCallback(
    // eslint-disable-next-line complexity
    async (operationResults: OperationResult[]) => {
      // Filter results that are either MULTIPOLYLINE or TRIANGULATED_SURFACE
      const shapeableResults = operationResults.filter(
        (result) =>
          result[0]?.type === ResultType.MULTIPOLYLINE ||
          result[0]?.type === ResultType.TRIANGULATED_SURFACE
      );

      if (shapeableResults.length === 0) {
        toast({
          title: t('project:properties.create-shape.error'),
          status: 'error',
        });
        return;
      }

      const unitScale = viewer?.model?.getUnitScale() ?? 1;
      let successCount = 0;
      let errorCount = 0;

      // Create a shape for each result
      for (const shapeResult of shapeableResults) {
        const result = shapeResult[0] as
          | MultiPolylineResult
          | TriangulatedSurfaceResult;
        const shapeType =
          result.type === ResultType.MULTIPOLYLINE
            ? ShapeDrawingResultType.LineString
            : ShapeDrawingResultType.Polygon;
        const shapeName = getNewShapeName('', shapeType);
        const input = getShapeInputFromResult(
          result,
          projectId,
          shapeName,
          unitScale
        );

        if (!input) continue;

        const { error } = await createShapeMutation({ input });
        if (error) {
          errorCount++;
        } else {
          successCount++;
        }
      }

      if (errorCount > 0 && successCount === 0) {
        toast({
          title: t('project:properties.create-shape.error'),
          status: 'error',
        });
      } else if (errorCount > 0) {
        toast({
          title: t('project:properties.create-shape.success'),
          description: `Created ${successCount} shapes, ${errorCount} failed`,
          status: 'warning',
        });
      } else {
        toast({
          title: t('project:properties.create-shape.success'),
          description:
            successCount > 1 ? `Created ${successCount} shapes` : undefined,
          status: 'success',
        });
      }
    },
    [createShapeMutation, getNewShapeName, projectId, t, toast, viewer]
  );

  const cleanedProjectSettings = useMemo(() => {
    return {
      maxTrianglesForComplexAlgorithm:
        projectSettings?.polygonCalculationLimit || 5000,
      globalOffset: [
        projectSettings?.viewerGlobalOffset?.x || 0,
        projectSettings?.viewerGlobalOffset?.y || 0,
        projectSettings?.viewerGlobalOffset?.z || 0,
      ] as Point,
    };
  }, [projectSettings]);

  useEffect(() => {
    // eslint-disable-next-line complexity
    async function resolve() {
      const id = Math.random().toString();
      setIsLoading(true, id);

      // Defer until properties context is fully loaded (prevents empty results for BIM)
      if (!isAllPropertiesLoaded) {
        setIsLoading(false, id);
        return;
      }

      // Skip calculation only if the selection is exactly the root node (dbId 1)
      const allSelectedIds = Object.values(selectedDbIds).flatMap((v) => v);
      const isOnlyRootSelected =
        allSelectedIds.length > 0 && allSelectedIds.every((dbId) => dbId === 1);
      if (isOnlyRootSelected) {
        setIsLoading(false, id);
        return;
      }

      // Use the live/current propertyContext for calculations
      // Historical context is used for diff-only visualizations on demand
      const contextForCalculations = propertyContext;

      const results = new Map<GeometricAlgorithmType, OperationResult[]>();
      for (const algorithmType of geometricAlgorithms) {
        const operationResult = await calculateGeometry(
          algorithmType,
          convertToElementIdentifierDbidType(selectedDbIds),
          contextForCalculations,
          cleanedProjectSettings
        );
        if (operationResult.length > 0) {
          results.set(algorithmType, operationResult);
        }
      }
      setAlgorithmResults(results);
      setIsLoading(false, id);
    }
    resolve();
  }, [
    geometricAlgorithms,
    isAllPropertiesLoaded,
    cleanedProjectSettings,
    propertyContext,
    newPropertyContextForDiff,
    selectedDbIds,
    setIsLoading,
  ]);

  const { locationResults, calculationResults } = useMemo(
    () => separateLocationAlgorithms(algorithmResults),
    [algorithmResults]
  );

  const geometricPropertyFields = useMemo(() => {
    const operationResults: OperationResult[] = [
      ...calculationResults.values(),
    ].flat();
    const propertyResults = groupBy(operationResults, ([, context]) => {
      if (!context.algorithmType) {
        return '';
      }
      const mapping = getMappingForTargetType(context.targetType as any);
      const geometricPropertyType = mapping?.ReverseMap.get(
        context.algorithmType as any
      );
      return (geometricPropertyType as string) ?? '';
    });

    return Object.entries(propertyResults)
      .filter(([geometricPropertyType, opResults]) => {
        if (!geometricPropertyType) {
          return false;
        }
        return opResults.some((result) => result[0] !== undefined);
      })
      .map(([geometricPropertyType, opResults]) => {
        let resultValue = sumBy(opResults, (result) => result[0]?.value ?? 0);
        let resultUnits = opResults.find(
          (result) => result[0]?.units !== undefined
        )?.[0]?.units;

        if (enableUnitSettings && projectSettings?.unitSettings) {
          const resultQuantity = convertSIQuantity(
            { value: resultValue, units: resultUnits || null },
            projectSettings.unitSettings
          );

          resultValue = resultQuantity.value as number;
          resultUnits = resultQuantity.units || undefined;
        }

        // Check if any result can be converted to a shape (line or polygon)
        const hasShapeableResult = opResults.some(
          (result) =>
            result[0]?.type === ResultType.MULTIPOLYLINE ||
            result[0]?.type === ResultType.TRIANGULATED_SURFACE
        );

        return {
          key: displayNameFromProperty(geometricPropertyType, t),
          originalKey: geometricPropertyType,
          value: (
            <Button
              fontSize="xs"
              fontWeight="bold"
              variant="link"
              _hover={{
                fontWeight: 'bold',
              }}
              onClick={() => {
                setVisualizedAlgorithmResult(opResults);
              }}
            >
              <Text noOfLines={1} wordBreak="break-all" color="teal.400">
                {round(
                  resultValue,
                  getPrecisionFromUnitSettings(
                    resultUnits,
                    projectSettings?.unitSettings
                  )
                )}{' '}
                {formatUnit(resultUnits)}
              </Text>
            </Button>
          ),
          onCreateShape:
            enableCreateShapesFromAlgorithmResults && hasShapeableResult
              ? () => handleCreateShape(opResults)
              : undefined,
        };
      })
      .sort((a, b) => (isNil(a) || isNil(b) ? 0 : a.key.localeCompare(b.key)))
      .filter((field) => field !== null) as KeyValue[];
  }, [
    calculationResults,
    enableUnitSettings,
    handleCreateShape,
    projectSettings?.unitSettings,
    t,
  ]);

  const calculationPropertyFields = Array.from(calculationResults)
    .sort(([propertyTypeA], [propertyTypeB]) =>
      propertyTypeA.localeCompare(propertyTypeB)
    )
    .filter(([geometricPropertyType, operationResults]) => {
      if (!geometricPropertyType) {
        return false;
      }
      return operationResults.some((result) => result[0] !== undefined);
    })
    .map(([algorithmType, operationResults]) => {
      let resultValue = sumBy(
        operationResults,
        (result) => result[0]?.value ?? 0
      );
      let resultUnits = operationResults.find(
        (result) => result[0]?.units !== undefined
      )?.[0]?.units;

      if (projectSettings?.unitSettings) {
        const resultQuantity = convertSIQuantity(
          { value: resultValue, units: resultUnits || null },
          projectSettings.unitSettings
        );

        resultValue = resultQuantity.value as number;
        resultUnits = resultQuantity.units || undefined;
      }

      // Check if any result can be converted to a shape (line or polygon)
      const hasShapeableResult = operationResults.some(
        (result) =>
          result[0]?.type === ResultType.MULTIPOLYLINE ||
          result[0]?.type === ResultType.TRIANGULATED_SURFACE
      );

      return {
        key: displayNameFromAlgorithm(algorithmType, t),
        originalKey: algorithmType,
        value: (
          <Button
            fontSize="xs"
            fontWeight="bold"
            variant="link"
            _hover={{
              fontWeight: 'bold',
            }}
            onClick={() => {
              setVisualizedAlgorithmResult(operationResults);
            }}
          >
            <Text noOfLines={1} wordBreak="break-all" color="teal.400">
              {round(
                resultValue,
                getPrecisionFromUnitSettings(
                  resultUnits,
                  projectSettings?.unitSettings
                )
              )}{' '}
              {formatUnit(resultUnits)}
            </Text>
          </Button>
        ),
        onCreateShape:
          enableCreateShapesFromAlgorithmResults && hasShapeableResult
            ? () => handleCreateShape(operationResults)
            : undefined,
      };
    })
    .filter((field) => field !== null) as KeyValue[];

  // Add diff-only algorithm rows when no live results exist (e.g., element deleted in current state)
  const diffOnlyCalculationFields: KeyValue[] = useMemo(() => {
    const existingKeys = new Set(
      (Array.isArray(calculationPropertyFields)
        ? calculationPropertyFields
        : []
      ).map((kv) => kv.originalKey ?? kv.key)
    );
    // Suppress diff-only rows for BIM elements: they have no diff for calculations
    const entries = Object.entries(propertyDiffMap ?? {})
      // Heuristic: non-friendly keys (no ':') are algorithm diffs
      .filter(([k]) => !k.includes(':'))
      // Exclude location algorithms here; they will be handled in location diff-only
      .filter(([k]) => !k.toLowerCase().includes('location'))
      .filter(([k]) => !existingKeys.has(k));
    return entries.map(([k, d]) => {
      const hasOld = typeof d?.old === 'number';
      const hasNew = typeof d?.new === 'number';
      return {
        key: k,
        originalKey: k,
        elementKey: 'diff-only',
        value: (
          <DiffNumber
            oldValue={hasOld ? (d?.old as number) : undefined}
            newValue={hasNew ? (d?.new as number) : undefined}
            precision={2}
            onClick={async () => {
              const ctx =
                hasOld && !hasNew
                  ? referencePropertyContext
                  : newPropertyContextForDiff;
              if (!ctx) return;
              const results = await calculateGeometry(
                k as any,
                convertToElementIdentifierDbidType(selectedDbIds),
                ctx,
                cleanedProjectSettings
              );
              setVisualizedAlgorithmResult(results);
            }}
          />
        ),
      };
    });
  }, [
    calculationPropertyFields,
    propertyDiffMap,
    newPropertyContextForDiff,
    referencePropertyContext,
    selectedDbIds,
    cleanedProjectSettings,
  ]);

  // Build final calculation fields with diffs applied (single source of truth for rendering)
  const finalCalculationFields: KeyValue[] = useMemo(() => {
    // eslint-disable-next-line complexity
    const mapItem = (kv: KeyValue): KeyValue => {
      const diff = propertyDiffMap?.[kv.originalKey ?? kv.key];
      const hasOld = typeof diff?.old === 'number';
      const hasNew = typeof diff?.new === 'number';
      const eventType = computeEventType(diff);

      if ((kv as any).elementKey === 'diff-only' || !diff) {
        return { ...kv, eventType };
      }
      if (hasNew && !hasOld) return { ...kv, eventType };

      const value =
        hasOld && !hasNew ? (
          <DiffNumber
            oldValue={diff?.old as number}
            precision={2}
            onClick={() => {
              if (newPropertyContextForDiff) {
                calculateGeometry(
                  (kv.originalKey ?? kv.key) as any,
                  convertToElementIdentifierDbidType(selectedDbIds),
                  newPropertyContextForDiff,
                  cleanedProjectSettings
                ).then(setVisualizedAlgorithmResult);
              }
            }}
          />
        ) : (
          <DiffNumber
            oldValue={hasOld ? (diff?.old as number) : undefined}
            newValue={hasNew ? (diff?.new as number) : undefined}
            precision={2}
            onClick={() => {
              if (newPropertyContextForDiff) {
                calculateGeometry(
                  (kv.originalKey ?? kv.key) as any,
                  convertToElementIdentifierDbidType(selectedDbIds),
                  newPropertyContextForDiff,
                  cleanedProjectSettings
                ).then(setVisualizedAlgorithmResult);
              }
            }}
          />
        );

      return { ...kv, eventType, value };
    };

    return [...calculationPropertyFields, ...diffOnlyCalculationFields].map(
      mapItem
    );
  }, [
    calculationPropertyFields,
    diffOnlyCalculationFields,
    propertyDiffMap,
    newPropertyContextForDiff,
    selectedDbIds,
    cleanedProjectSettings,
  ]);

  // Map algorithm originalKey => rendered value (to reuse for quantities)
  const algorithmValueByOriginalKey = useMemo(() => {
    const map = new Map<string, React.ReactElement>();
    for (const kv of finalCalculationFields) {
      const key = kv.originalKey ?? kv.key;
      const val = kv.value;
      if (
        val &&
        val !== null &&
        typeof val !== 'string' &&
        typeof val !== 'number'
      ) {
        map.set(key, val as React.ReactElement);
      }
    }
    return map;
  }, [finalCalculationFields]);

  // Map geometric property type => algorithm originalKey used in calculationResults
  const propertyNameToAlgorithmKey = useMemo(() => {
    const map = new Map<string, string>();
    calculationResults.forEach((operationResults, algorithmType) => {
      const sample = operationResults.find((r) => r[1] !== undefined);
      const context = sample?.[1];
      if (!context?.targetType) return;
      const mapping = getMappingForTargetType(context.targetType as any);
      const propertyType = mapping?.ReverseMap.get(algorithmType as any) as
        | string
        | undefined;
      if (!propertyType) return;
      map.set(propertyType, algorithmType as any);
    });
    return map;
  }, [calculationResults]);

  // Reuse exact calculation result component for the corresponding quantity
  const quantityFieldsWithDiff: KeyValue[] = useMemo(() => {
    return geometricPropertyFields.map((kv) => {
      const propertyType = kv.originalKey ?? kv.key;
      const algoKey = propertyNameToAlgorithmKey.get(propertyType);
      if (!algoKey) return kv;
      const reused = algorithmValueByOriginalKey.get(algoKey);
      const diff = propertyDiffMap?.[algoKey];
      const eventType = computeEventType(diff);
      if (!reused) return eventType ? { ...kv, eventType } : kv;
      return { ...kv, value: reused, eventType };
    });
  }, [
    geometricPropertyFields,
    propertyNameToAlgorithmKey,
    algorithmValueByOriginalKey,
    propertyDiffMap,
  ]);

  const locationPropertyFields = Array.from(locationResults)
    .sort(([propertyTypeA], [propertyTypeB]) =>
      propertyTypeA.localeCompare(propertyTypeB)
    )
    .filter(([geometricPropertyType, operationResults]) => {
      if (!geometricPropertyType) {
        return false;
      }
      return operationResults.some((result) => result[0] !== undefined);
    })
    .map(([algorithmType, operationResults]) => {
      let resultValue = sumBy(
        operationResults,
        (result) => result[0]?.value ?? 0
      );
      let resultUnits = operationResults.find(
        (result) => result[0]?.units !== undefined
      )?.[0]?.units;

      if (projectSettings?.unitSettings) {
        const resultQuantity = convertSIQuantity(
          { value: resultValue, units: resultUnits || null },
          projectSettings.unitSettings
        );

        resultValue = resultQuantity.value as number;
        resultUnits = resultQuantity.units || undefined;
      }

      return {
        key: displayNameFromAlgorithm(algorithmType, t),
        originalKey: algorithmType,
        value: (
          <Button
            fontSize="xs"
            fontWeight="bold"
            variant="link"
            _hover={{
              fontWeight: 'bold',
            }}
            onClick={() => {
              setVisualizedAlgorithmResult(operationResults);
            }}
          >
            <Text noOfLines={1} wordBreak="break-all" color="teal.400">
              {round(
                resultValue,
                getPrecisionFromUnitSettings(
                  resultUnits,
                  projectSettings?.unitSettings
                )
              )}{' '}
              {formatUnit(resultUnits)}
            </Text>
          </Button>
        ),
      };
    })
    .filter((field) => field !== null) as KeyValue[];

  // Diff-only location algorithm rows (when element deleted in current state etc.)
  const diffOnlyLocationFields: KeyValue[] = useMemo(() => {
    const existingKeys = new Set(
      (Array.isArray(locationPropertyFields) ? locationPropertyFields : []).map(
        (kv) => kv.originalKey ?? kv.key
      )
    );
    // Suppress diff-only rows for BIM elements: they have no diff for locations
    const entries = Object.entries(propertyDiffMap ?? {})
      .filter(([k]) => !k.includes(':'))
      .filter(([k]) => k.toLowerCase().includes('location'))
      .filter(([k]) => !existingKeys.has(k));
    return entries.map(([k, d]) => {
      const hasOld = typeof d?.old === 'number';
      const hasNew = typeof d?.new === 'number';
      return {
        key: k,
        originalKey: k,
        elementKey: 'diff-only',
        value: (
          <DiffNumber
            oldValue={hasOld ? (d?.old as number) : undefined}
            newValue={hasNew ? (d?.new as number) : undefined}
            precision={2}
            onClick={async () => {
              const ctx =
                hasOld && !hasNew
                  ? referencePropertyContext
                  : newPropertyContextForDiff;
              if (!ctx) return;
              const results = await calculateGeometry(
                k as any,
                convertToElementIdentifierDbidType(selectedDbIds),
                ctx,
                cleanedProjectSettings
              );
              setVisualizedAlgorithmResult(results);
            }}
          />
        ),
      };
    });
  }, [
    locationPropertyFields,
    propertyDiffMap,
    newPropertyContextForDiff,
    referencePropertyContext,
    selectedDbIds,
    cleanedProjectSettings,
  ]);

  const getDragData = (key: string, originalKey?: string) => {
    invariant(typeof originalKey === 'string', 'originalKey must be a string');
    return formatDragData({
      category: 'Sparkel properties',
      propertyName: originalKey,
      displayName: key,
    });
  };

  const LocationPropertiesPanel = ({
    selectedDbIds,
    isLoading,
    locationPropertyFields,
    getDragData,
  }: {
    selectedDbIds: DbIdsPerModel;
    isLoading: boolean;
    locationPropertyFields: KeyValue[];
    getDragData: (key: string, originalKey?: string) => string;
  }): React.ReactElement => {
    const { viewer } = useViewer();

    return (
      <AccordionPanel pb={0} pt={0} pr={0}>
        {isLoading ? (
          <Center>
            <Spinner color="brand.400" margin={'auto'} />
          </Center>
        ) : (
          <StyledKeyValuesProperties
            keyValues={locationPropertyFields}
            getDragData={getDragData}
          />
        )}
      </AccordionPanel>
    );
  };

  return (
    <VStack align="stretch" w="full" spacing={0} paddingRight={0}>
      <OnDemandGeometryVisualization
        operationResults={visualizedAlgorithmResult}
      />
      <Accordion
        pl={0}
        variant={'panel-accordion'}
        allowMultiple
        index={expandedAccordions}
        onChange={(expandedIndex) => setExpandedAccordions(expandedIndex)}
      >
        <AccordionItem key="information">
          <AccordionButton>
            <Box flex="1" textAlign="left">
              Information
            </Box>
            <AccordionIcon />
          </AccordionButton>
          <FriendlyPropertiesPanel
            properties={referenceResolvedProperties ?? properties}
            diffMap={propertyDiffMap}
          />
        </AccordionItem>
        <AccordionItem key="quantities">
          <AccordionButton>
            <Box flex="1" textAlign="left">
              {t('quantities')}
            </Box>
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel pb={0} pt={0} pr={0}>
            {isLoading ? (
              <Center>
                <Spinner color="brand.400" margin={'auto'} />
              </Center>
            ) : (
              <StyledKeyValuesProperties
                keyValues={quantityFieldsWithDiff}
                getDragData={getDragData}
              />
            )}
          </AccordionPanel>
        </AccordionItem>
        {!isFreeContractor && (
          <AccordionItem key="location">
            <AccordionButton>
              <Box flex="1" textAlign="left">
                {t('location')}
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <LocationPropertiesPanel
              selectedDbIds={selectedDbIds}
              isLoading={isLoading}
              locationPropertyFields={[
                ...locationPropertyFields,
                ...diffOnlyLocationFields,
                // eslint-disable-next-line complexity
              ].map((kv) => {
                const diff = propertyDiffMap?.[kv.originalKey ?? kv.key];
                const hasOld = typeof diff?.old === 'number';
                const hasNew = typeof diff?.new === 'number';
                let eventType: EventType | undefined;
                if (hasNew && !hasOld) {
                  eventType = EventType.Created;
                } else if (hasOld && !hasNew) {
                  eventType = EventType.Deleted;
                } else if (
                  hasOld &&
                  hasNew &&
                  Math.abs((diff.old as number) - (diff.new as number)) >
                    0.000001
                ) {
                  eventType = EventType.Modified;
                }
                // Preserve injected diff-only rows but attach eventType for styling
                if ((kv as any).elementKey === 'diff-only') {
                  return { ...kv, eventType } as KeyValue;
                }
                if (!diff) return kv;
                // Created-only: keep the original button rendering, but include eventType
                if (hasNew && !hasOld)
                  return {
                    ...kv,
                    eventType,
                  } as KeyValue;
                return {
                  ...kv,
                  eventType,
                  value:
                    hasOld && !hasNew ? (
                      <DiffNumber
                        oldValue={diff!.old as number}
                        precision={2}
                        onClick={() => {
                          // For deleted location calculations, show historical visualization
                          if (newPropertyContextForDiff) {
                            calculateGeometry(
                              kv.originalKey as any,
                              convertToElementIdentifierDbidType(selectedDbIds),
                              newPropertyContextForDiff,
                              cleanedProjectSettings
                            ).then(setVisualizedAlgorithmResult);
                          }
                        }}
                      />
                    ) : (
                      <DiffNumber
                        oldValue={hasOld ? (diff.old as number) : undefined}
                        newValue={hasNew ? (diff.new as number) : undefined}
                        precision={2}
                        onClick={() => {
                          // For modified location calculations, show current visualization
                          if (newPropertyContextForDiff) {
                            calculateGeometry(
                              kv.originalKey as any,
                              convertToElementIdentifierDbidType(selectedDbIds),
                              newPropertyContextForDiff,
                              cleanedProjectSettings
                            ).then(setVisualizedAlgorithmResult);
                          }
                        }}
                      />
                    ),
                };
              })}
              getDragData={getDragData}
            />
          </AccordionItem>
        )}
        {!isFreeContractor && (
          <AccordionItem key="calculations">
            <AccordionButton>
              <Box flex="1" textAlign="left">
                {t('calculations')}
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel pb={0} pt={0} pr={0}>
              {isLoading ? (
                <Center>
                  <Spinner color="brand.400" margin={'auto'} />
                </Center>
              ) : (
                <StyledKeyValuesProperties
                  keyValues={finalCalculationFields}
                  getDragData={getDragData}
                />
              )}
            </AccordionPanel>
          </AccordionItem>
        )}
      </Accordion>
    </VStack>
  );
}

const FriendlyPropertiesPanel = ({
  properties,
  diffMap,
}: {
  properties: Property[];
  diffMap?: Record<string, { old?: unknown; new?: unknown }>;
}): React.ReactElement => {
  const { t: translate } = useTranslation(['common', 'categories']);
  const { mappedProperties, displayDiffMap } = useMemo(() => {
    const mapped: Property[] = [];
    const originalToDisplay: Record<string, string> = {};
    properties.forEach((property) => {
      const friendlyProperty = FRIENDLY_PROPERTIES.find(
        (friendlyProperty) =>
          friendlyProperty[0] === property.attribute.category &&
          friendlyProperty[1] === property.attribute.name
      );
      if (!friendlyProperty) return;
      const originalKey = `${property.attribute.category}:${property.attribute.name}`;
      if (
        property.attribute.category === 'Element' &&
        property.attribute.name === 'IfcClass'
      ) {
        const mappedCategoryValue = getCategoriesWithFriendlyName(
          translate
        ).find((c) => c.modernValues.includes(`${property.value}`));
        if (isNil(mappedCategoryValue)) return;
        const mappedProperty: Property = {
          ...property,
          type: PropertyType.Text,
          attribute: {
            category: friendlyProperty[0],
            name: friendlyProperty[1],
          },
          value: mappedCategoryValue.friendlyName,
        };
        mapped.push(mappedProperty);
        originalToDisplay[originalKey] = mappedProperty.attribute.name;
        return;
      }
      const displayName = displayNameFromFriendlyAttribute(
        friendlyProperty[1] as FriendlyAttribute,
        translate
      );
      const mappedProperty: Property = {
        ...property,
        attribute: {
          category: friendlyProperty[0],
          name: friendlyProperty[1],
        },
      };
      mapped.push(mappedProperty);
      originalToDisplay[originalKey] = displayName;
    });
    const outDiff: Record<string, { old?: unknown; new?: unknown }> = {};
    if (diffMap) {
      Object.entries(diffMap).forEach(([origKey, d]) => {
        // Only include friendly property diffs (Category:Name), exclude algorithms and Sparkel attributes
        if (!origKey.includes(':')) return;
        if (origKey.startsWith('Sparkel:')) return;
        let displayName = originalToDisplay[origKey];
        if (!displayName) {
          // Fallback: include ONLY if this property is part of FRIENDLY_PROPERTIES
          const [cat, rawName] = origKey.split(':');
          const friendly = FRIENDLY_PROPERTIES.find(
            ([c, attr]) => c === cat && String(attr) === rawName
          );
          if (!friendly) return; // Skip non-friendly BIM properties like Element GUID
          displayName = displayNameFromFriendlyAttribute(
            friendly[1] as FriendlyAttribute,
            translate
          );
        }
        outDiff[displayName] = d;
      });
    }
    // Ensure diff-only properties (present in diff but not in reference) are shown
    const presentNames = new Set(mapped.map((p) => p.attribute.name));
    Object.entries(outDiff).forEach(([displayName, d]) => {
      if (!presentNames.has(displayName)) {
        // Infer category from known friendly list; if not found, skip
        const friendly = FRIENDLY_PROPERTIES.find(
          ([, attr]) =>
            displayNameFromFriendlyAttribute(attr, translate) === displayName
        );
        if (!friendly) return;
        const category = friendly[0];
        mapped.push({
          attribute: { category, name: displayName },
          type: PropertyType.Text,
          value: String(d.new ?? ''),
        } as Property);
        presentNames.add(displayName);
      }
    });
    return { mappedProperties: mapped, displayDiffMap: outDiff };
  }, [properties, translate, diffMap]);

  if (mappedProperties.length === 0) {
    return (
      <AccordionPanel pb={0} pt={0}>
        <PlaceholderText>No properties</PlaceholderText>;
      </AccordionPanel>
    );
  }

  return (
    <AccordionPanel pb={0} pt={0} pr={0}>
      <PropertiesAccordionBody
        propertiesForCategory={mappedProperties}
        diffMap={displayDiffMap}
      />
    </AccordionPanel>
  );
};
